/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { MutableRef, useLayoutEffect, useMemo, useRef, useCallback } from 'preact/hooks';
import { ComponentChildren } from 'preact';

import { Text } from '../UNSAFE_Text';
import { classNames } from '../utils/UNSAFE_classNames';
import { useId } from '../hooks/UNSAFE_useId';
import { mergeProps } from '../utils/UNSAFE_mergeProps';
import { usePress } from '../hooks/UNSAFE_usePress';
import { useUser } from '../hooks/UNSAFE_useUser';
import { useComponentTheme } from '../hooks/UNSAFE_useComponentTheme';
import { mergeInterpolations } from '../utils/UNSAFE_mergeInterpolations';
import { flexitemInterpolations } from '../utils/UNSAFE_interpolations/flexitem';
import type { FlexitemProps } from '../utils/UNSAFE_interpolations/flexitem';
import { useInteractionStyle } from '../hooks/UNSAFE_useInteractionStyle';

import { useMenuContext } from './useMenuContext';
import { ForwardedRef, forwardRef } from 'preact/compat';
import { getMenuItemClickChangeKey, getOpenSubmenuEvent } from './menuUtils';
import { useMenuContainerContext } from './useMenuContainerContext';
import { useSubmenuItemHover } from './useSubmenuItemHover';
import { MenuItemStyles, MenuItemVariantOptions } from './themes/MenuItemStyles.css';
import { MenuItemRedwoodTheme } from './themes/redwood/MenuItemTheme';

type MenuItemProps = BaseBaseMenuItemProps;

type BaseBaseMenuItemProps = {
  label: string;
  onAction?: () => void;
  startIcon?: ComponentChildren;
  endIcon?: ComponentChildren;
  isDisabled?: boolean;
  variant?: 'standard' | 'destructive';
};

export function MenuItem(props: MenuItemProps) {
  const uniqueID = useId();
  const id = useMemo(() => `oj-menu-item-${uniqueID}`, [uniqueID]);
  return <BaseMenuItem {...props} role="menuitem" type="simple" id={id}></BaseMenuItem>;
}
//TODO:Omit<T, K> utility type does not distribute over union types in T. Have to check what can we do for
//the discriminated union to be working. JET-57191
type BaseMenuItemProps = (SimpleBaseMenuItem | SelectBaseMenuItem | SubmenuBaseMenuItem) &
  Omit<BaseBaseMenuItemProps, 'onAction'> & {
    id: string;
    menuItemLabelWeight?: 'normal' | 'bold';
    onAction?: (e: Event) => void;
  };

type SimpleBaseMenuItem = {
  type: 'simple';
  role: 'menuitem';
} & ExcludedSubmenuProps &
  ExcludedSelectProps;

type SelectBaseMenuItem = {
  type: 'select';
  role: 'menuitemcheckbox' | 'menuitemradio';
  isChecked?: boolean;
} & ExcludedSubmenuProps;

type SubmenuBaseMenuItem = {
  type: 'submenu';
  role: 'menuitem';
} & IncludedSubmenuProps &
  ExcludedSelectProps;

type ExcludedSubmenuProps = {
  hoverInfo?: never;
  openSubmenu?: never;
  isSubmenuOpen?: never;
  submenuId?: never;
  submenuRef?: never;
};
type IncludedSubmenuProps = {
  hoverInfo?: {
    hoverProps: Record<string, any>;
    hoverStatus: MutableRef<{ recentClose: boolean; pendingUnhover: boolean }>;
  };
  openSubmenu?: () => void;
  isSubmenuOpen?: boolean;
  submenuId?: string;
  submenuRef?: MutableRef<HTMLDivElement | null>;
};

type ExcludedSelectProps = {
  isChecked?: never;
};

//TODO: We have to comeback to this and see if we create new components to represent different types on menuItems
//instead of using type prop. It also includes a new definition on prop types. This also includes the exposing of id
//that is used by submenus. JET-57191
export const BaseMenuItem = forwardRef(
  (
    {
      id,
      label,
      onAction,
      startIcon,
      endIcon,
      role,
      isChecked,
      isDisabled = false,
      variant = 'standard',
      type,
      hoverInfo,
      openSubmenu,
      menuItemLabelWeight = 'normal',
      isSubmenuOpen,
      submenuId,
      submenuRef
    }: BaseMenuItemProps,
    ref?: ForwardedRef<HTMLAnchorElement>
  ) => {
    const { onClose } = useMenuContext();
    const {
      currentKey,
      changeKey,
      showFocusRing,
      menuContainerRef,
      menuContainerGeneralInformation,
      isContainerFocused
    } = useMenuContainerContext();

    const menuItemRef = useRef<HTMLAnchorElement | null>(null);
    const { direction } = useUser();

    const isCurrent = currentKey === id;

    const handleItemSelection = useCallback(
      (e: Event) => {
        //We don't want to close the menu if the element is a submenu one
        if (type !== 'submenu') {
          onClose?.({ reason: 'itemAction' });
        }
        onAction?.(e);
      },
      [type, onClose, onAction]
    );

    /*This hook is not used to just know is something is hover or not. Actual callbacks are run when mouse enter
    or leave the menu item (to focus items when hovering and open submenus if the item is a submenu).  We don't use
    useInteractionStyle hook for hovering since "hovering style" is more of a "focus" style in this context */
    const { hoverProps } = useSubmenuItemHover({
      onMouseEnter: (e: MouseEvent) => {
        if (
          !(
            hoverInfo?.hoverStatus.current.recentClose ||
            hoverInfo?.hoverStatus.current.pendingUnhover
          )
        ) {
          if (hoverInfo && menuContainerGeneralInformation.current.wasMouseMoved) {
            hoverInfo.hoverStatus.current.pendingUnhover = true;
          }
          // Mouse needs to be already moved to trigger mouse enter event
          if (menuContainerGeneralInformation.current.wasMouseMoved) {
            hoverInfo?.hoverProps['onMouseEnter'](e);
            changeKey({
              key: id,
              reason: 'mouseEnter'
            });
          }
        }
      },
      onMouseLeave: (e: MouseEvent) => {
        if (hoverInfo) {
          hoverInfo.hoverStatus.current.pendingUnhover = false;
        }
        //We check if new hover element is part of the menuContainer or submenu is there is one
        if (
          menuContainerRef.current?.contains(e.relatedTarget as Element) ||
          submenuRef?.current?.contains(e.relatedTarget as Element)
        ) {
          menuContainerGeneralInformation.current.lastMouseLeaveMenuItemdRelatedTarget =
            e.relatedTarget as HTMLElement;
        }
      }
    });

    const { pressProps } = usePress(handleItemSelection, { isDisabled });

    useLayoutEffect(() => {
      if (isCurrent) {
        menuItemRef.current?.focus();
      }
    }, [isCurrent]);

    const { interactionProps } = useInteractionStyle(isDisabled);

    const {
      classes: menuItemContainerClasses,
      styles: {
        iconLabelContainer,
        iconContainer,
        startIconContainer,
        labelContainer,
        endIconContainer
      }
    } = useComponentTheme<MenuItemVariantOptions, MenuItemStyles>(MenuItemRedwoodTheme, {
      current: isCurrent ? 'isCurrent' : 'notCurrent',
      focusRing: showFocusRing ? 'isFocusRing' : 'notFocusRing',
      variant: isDisabled ? 'disabled' : variant,
      containerFocused: isContainerFocused ? 'isContainerFocused' : 'notContainerFocused',
      submenuOpen: !!isSubmenuOpen ? 'isSubmenuOpen' : 'notSubmenuOpen'
    });

    const flexItemStyleInterpolations = mergeInterpolations<FlexitemProps>([
      ...Object.values(flexitemInterpolations)
    ]);
    const { class: labelContainerFlexItemClasses, ...labelContainerFlexItemStyles } =
      flexItemStyleInterpolations({
        flex: '1 1 auto',
        alignSelf: 'center'
      });
    const { class: iconContainerFlexItemClasses, ...iconContainerFlexItemStyles } =
      flexItemStyleInterpolations({
        flex: '0 0 auto',
        alignSelf: 'center'
      });

    const startIconContainerClasses = classNames([
      iconContainerFlexItemClasses,
      iconLabelContainer,
      iconContainer,
      startIconContainer
    ]);

    const labelContainerClasses = classNames([
      labelContainerFlexItemClasses,
      iconLabelContainer,
      labelContainer
    ]);

    const endIconContainerClasses = classNames([
      iconContainerFlexItemClasses,
      iconLabelContainer,
      iconContainer,
      endIconContainer
    ]);

    return (
      <div
        role="presentation"
        {...(submenuId !== undefined && type === 'submenu' && { 'aria-owns': submenuId })}>
        <a
          ref={(node: HTMLAnchorElement | null) => {
            menuItemRef.current = node;
            if (typeof ref === 'function') {
              ref(node);
            } else if (ref) {
              ref.current = node;
            }
          }}
          class={menuItemContainerClasses}
          {...mergeProps(
            interactionProps,
            hoverProps,
            pressProps,
            getOpenSubmenuEvent(direction, openSubmenu),
            getMenuItemClickChangeKey(id, changeKey)
          )}
          role={role}
          data-oj-menu-item-key={id}
          {...(type === 'submenu' && { 'aria-haspopup': true, 'aria-expanded': !!isSubmenuOpen })}
          {...(isDisabled && { 'aria-disabled': true })}
          {...(type === 'select' && { 'aria-checked': !!isChecked })}
          tabIndex={isCurrent ? 0 : -1}>
          {startIcon && (
            <span style={iconContainerFlexItemStyles} class={startIconContainerClasses}>
              {startIcon}
            </span>
          )}
          <span style={labelContainerFlexItemStyles} class={labelContainerClasses}>
            <Text variant="inherit" size="md" weight={menuItemLabelWeight}>
              {label}
            </Text>
          </span>
          {endIcon && (
            <span style={iconContainerFlexItemStyles} class={endIconContainerClasses}>
              {endIcon}
            </span>
          )}
        </a>
      </div>
    );
  }
);
